const ITERATIONS = 210_000;

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  bytes.forEach((byte) => { binary += String.fromCharCode(byte); });
  return btoa(binary);
}

function base64ToBytes(value: string): Uint8Array {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

function strictBytes(bytes: Uint8Array): Uint8Array<ArrayBuffer> {
  return new Uint8Array(Array.from(bytes));
}

async function deriveKey(masterPassword: string, salt: Uint8Array, iterations = ITERATIONS): Promise<CryptoKey> {
  const material = await crypto.subtle.importKey('raw', new TextEncoder().encode(masterPassword), 'PBKDF2', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: strictBytes(salt), iterations, hash: 'SHA-256' },
    material,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

export async function encryptSecret(secret: string, masterPassword: string): Promise<{ encrypted: string; iv: string; salt: string; iterations: number }> {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const key = await deriveKey(masterPassword, salt);
  const encrypted = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: strictBytes(iv) }, key, strictBytes(new TextEncoder().encode(secret)));
  return {
    encrypted: bytesToBase64(new Uint8Array(encrypted)),
    iv: bytesToBase64(iv),
    salt: bytesToBase64(salt),
    iterations: ITERATIONS
  };
}

export async function decryptSecret(encrypted: string, masterPassword: string, iv: string, salt: string, iterations: number): Promise<string> {
  const key = await deriveKey(masterPassword, base64ToBytes(salt), iterations);
  const decrypted = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: strictBytes(base64ToBytes(iv)) }, key, strictBytes(base64ToBytes(encrypted)));
  return new TextDecoder().decode(decrypted);
}

export function randomPassword(length = 20): string {
  const alphabet = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()-_=+[]{};:,.?';
  const bytes = crypto.getRandomValues(new Uint8Array(length));
  return Array.from(bytes, (byte) => alphabet[byte % alphabet.length]).join('');
}
